import React, { useEffect, useState } from 'react'
import { Card, Col, List, Row, Space, Tag } from 'antd'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import { StatusTag } from '../../../components/Shared'
import CustomList from '../../../components/Shared/CustomList'
import { getOverviewData } from '../../../api/modules/api-dashboard'
/**
 * @component OverviewData
 * @description
 * A dashboard widget that displays two panels:
 * - Upcoming sprints with deadlines.
 * - User-specific requests with their statuses.
 *
 * Fetches summary data using `getOverviewData()` from the backend API and renders it
 * using two Ant Design `Card` components, each with a styled `CustomList`.
 *
 * - Left panel shows a list of sprint deadlines with links to the sprint details.
 * - Right panel shows request status tags and request titles with links.
 *
 * Utilizes MobX's `observer` to automatically re-render if observable state (if added later) changes.
 *
 * @returns {React.ReactElement} A responsive two-column layout for sprint deadlines and request statuses.
 */
const OverviewData = observer(() => {
	const [overview, setOverview] = useState({
		sprints: [],
		requests: []
	})
	// Load the overview data
	useEffect(() => {
		try {
			const fetchData = async () => {
				const overviewResponse = await getOverviewData()
				setOverview(overviewResponse.data || {})
			}
			fetchData()
		} catch (error) {
			console.error('Failed to fetch overview data:', error)
		}
	}, [])

	return (
		<>
			<h3>Overview</h3>
			<p>Review your account status</p>
			<Row gutter={16}>
				<Col span={12}>
					<Card title="Upcoming Deadlines">
						<CustomList
							dataSource={overview.sprints}
							renderItem={(item) => (
								<List.Item>
									<Space>
										<Tag color="red">{item.deadline}</Tag>
										<Link to={`sprints/${item.id}/view`}>
											{item.name}
										</Link>
									</Space>
								</List.Item>
							)}
						/>
					</Card>
				</Col>

				<Col span={12}>
					<Card title="Your Requests">
						<CustomList
							dataSource={overview.requests}
							renderItem={(item) => (
								<List.Item>
									<Space>
										<StatusTag
											isShort={false}
											tag={item.status}
											tagType={'RequestStatusTag'}
											style={{
												marginRight: 0,
												padding: '0 3px'
											}}
										/>
										<Link to={`requests/${item.id}/view`}>
											{item.request}
										</Link>
									</Space>
								</List.Item>
							)}
						/>
					</Card>
				</Col>
			</Row>
		</>
	)
})

export default OverviewData
